import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { auditApi } from '../services/api';
import { AuditResult, AuditTask, TASK_TYPE_LABELS } from '../types';
import RiskBadge from '../components/RiskBadge';
import FindingsTable from '../components/FindingsTable';

function Report() {
  const { taskId } = useParams<{ taskId: string }>();
  const [result, setResult] = useState<AuditResult | null>(null);
  const [task, setTask] = useState<AuditTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      if (!taskId) return;

      try {
        const taskResponse = await auditApi.getTask(taskId);
        const taskData = taskResponse.data;
        setTask(taskData);

        if (taskData.status !== 'completed') {
          setError('审查任务尚未完成');
          setLoading(false);
          return;
        }

        const reportResponse = await auditApi.getReport(taskId, 'json');
        setResult(reportResponse.data.content);
      } catch (err: any) {
        setError(err.response?.data?.detail || '获取报告失败');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [taskId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="p-8 text-center text-gray-500">
        报告数据为空
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">
              审查报告
            </h1>
            <div className="mt-2 space-y-1">
              {task?.paper_title && (
                <p className="text-gray-700 truncate">
                  <span className="font-medium">论文：</span>
                  <Link
                    to={`/papers`}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                    title="返回论文列表"
                  >
                    {task.paper_title}
                  </Link>
                </p>
              )}
              <p className="text-gray-600">
                <span className="font-medium">审查类型：</span>
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                  {TASK_TYPE_LABELS[task?.task_type || ''] || task?.task_type || '未知'}
                </span>
              </p>
              <p className="text-gray-500 text-sm">
                任务 #{taskId?.slice(0, 8)}
              </p>
            </div>
          </div>
          <RiskBadge level={result.overall_risk_level} size="lg" />
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          摘要
        </h2>
        <p className="text-gray-700">{result.summary}</p>

        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {result.findings_count}
            </div>
            <div className="text-sm text-gray-600">总发现</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">
              {result.high_findings}
            </div>
            <div className="text-sm text-red-600">高风险</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {result.medium_findings}
            </div>
            <div className="text-sm text-yellow-600">中风险</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {result.low_findings}
            </div>
            <div className="text-sm text-blue-600">低风险</div>
          </div>
        </div>
      </div>

      {/* Findings */}
      {result.findings && result.findings.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            详细发现
          </h2>
          <FindingsTable findings={result.findings} />
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations && result.recommendations.length > 0 && (
        <div className="bg-blue-50 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-4">
            建议措施
          </h2>
          <ul className="space-y-2">
            {result.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-blue-200 text-blue-800 rounded-full text-sm font-medium mr-3">
                  {index + 1}
                </span>
                <span className="text-blue-800">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Report;
